/**
 * ProductTabs Component
 * Provides accessible tab navigation for product description/details sections
 * Supports keyboard navigation (Tab, Enter, Space, Arrow keys)
 */
class ProductTabs extends HTMLElement {
  constructor() {
    super()
  }

  connectedCallback() {
    this.tabs = this.querySelectorAll('[role="tab"]')
    this.panels = this.querySelectorAll('[role="tabpanel"]')

    // Bind event handlers
    this.addEventListener('click', this.handleClick.bind(this))
    this.addEventListener('keydown', this.handleKeydown.bind(this))
  }

  /**
   * Handle click events on tabs
   * @param {MouseEvent} event
   */
  handleClick(event) {
    const tab = event.target.closest('[role="tab"]')
    if (tab) {
      this.selectTab(tab)
    }
  }

  /**
   * Handle keyboard events for tab navigation
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    const tab = event.target.closest('[role="tab"]')
    if (!tab) return

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowRight':
        event.preventDefault()
        this.navigateTabs(event.key === 'ArrowLeft' ? -1 : 1, tab)
        break
      case 'Home':
        event.preventDefault()
        this.selectTab(this.tabs[0])
        this.tabs[0].focus()
        break
      case 'End':
        event.preventDefault()
        const lastTab = this.tabs[this.tabs.length - 1]
        this.selectTab(lastTab)
        lastTab.focus()
        break
      case 'Enter':
      case ' ':
        event.preventDefault()
        this.selectTab(tab)
        break
    }
  }

  /**
   * Navigate tabs with arrow keys
   * @param {number} direction - -1 for previous, 1 for next
   * @param {HTMLElement} currentTab - The currently focused tab
   */
  navigateTabs(direction, currentTab) {
    const tabsArray = Array.from(this.tabs)
    const currentIndex = tabsArray.indexOf(currentTab)
    let newIndex = currentIndex + direction

    // Wrap around
    if (newIndex < 0) {
      newIndex = tabsArray.length - 1
    } else if (newIndex >= tabsArray.length) {
      newIndex = 0
    }

    const newTab = tabsArray[newIndex]
    this.selectTab(newTab)
    newTab.focus()
  }

  /**
   * Select a tab and show its panel
   * @param {HTMLElement} selectedTab - The tab to select
   */
  selectTab(selectedTab) {
    const tabIndex = selectedTab.dataset.tabIndex

    // Update all tabs
    this.tabs.forEach((tab) => {
      const isSelected = tab === selectedTab
      tab.setAttribute('aria-selected', isSelected ? 'true' : 'false')
      tab.classList.toggle('active', isSelected)
    })

    // Update all panels
    this.panels.forEach((panel) => {
      const panelIndex = panel.dataset.contentIndex
      const isVisible = panelIndex === tabIndex
      panel.classList.toggle('hidden', !isVisible)
      panel.classList.toggle('block', isVisible)
    })
  }
}

customElements.define('product-tabs', ProductTabs)
